import React, { useState } from 'react';
import {
    Wallet, Save, Lock, X,
    AlertCircle, CheckCircle2, Loader2, IndianRupee
} from 'lucide-react';
import api from '../api/axiosInstance';

const AdvanceAndFinalize = ({ selectedMonth, payouts, onRefresh }) => {
    const [showAdvanceModal, setShowAdvanceModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [advanceForm, setAdvanceForm] = useState({
        employee_id: '',
        amount: '',
        notes: '',
        date: new Date().toISOString().split('T')[0]
    });

    // 1. Record a New Advance
    const handleRecordAdvance = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post('record_employee_advance.php', advanceForm);
            if (res.data.status === 'success') {
                setShowAdvanceModal(false);
                setAdvanceForm({ employee_id: '', amount: '', notes: '', date: new Date().toISOString().split('T')[0] });
                onRefresh();
            }
        } catch (err) { alert("Failed to record advance"); }
        finally { setLoading(false); }
    };

    // 2. Finalize Month (Lock Balances for Next Month)
    const handleFinalizeMonth = async () => {
        if (!window.confirm(`Are you sure you want to finalize ${selectedMonth}? This will carry forward all balances to the next month.`)) return;
        setLoading(true);
        try {
            const res = await api.post('finalize_monthly_ledger.php', {
                month_year: selectedMonth,
                payouts: payouts // Sending the calculated net_payables to lock them
            });
            if (res.data.status === 'success') {
                alert("Month Finalized! Balances moved to next month.");
                onRefresh();
            }
        } catch (err) { alert("Finalization failed"); }
        finally { setLoading(false); }
    };

    return (
        <div className="flex gap-4 mb-8">
            <button
                onClick={() => setShowAdvanceModal(true)}
                className="flex-1 bg-white border-2 border-slate-900 text-slate-900 py-4 rounded-3xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-slate-50 transition-all"
            >
                <Wallet size={18} /> Give Advance (Cash Out)
            </button>

            <button
                onClick={handleFinalizeMonth}
                disabled={loading}
                className="flex-1 bg-slate-900 text-white py-4 rounded-3xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-emerald-600 transition-all shadow-xl"
            >
                {loading ? <Loader2 className="animate-spin" /> : <Lock size={18} />} Finalize {selectedMonth}
            </button>

            {/* --- ADVANCE MODAL --- */}
            {showAdvanceModal && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleRecordAdvance} className="bg-white w-full max-w-md rounded-[48px] shadow-2xl p-12 space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-black italic tracking-tighter uppercase">Issue Advance</h2>
                            <button type="button" onClick={() => setShowAdvanceModal(false)} className="p-2 bg-slate-100 rounded-full"><X size={20} /></button>
                        </div>

                        <div className="space-y-4">
                            <select
                                required
                                className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none border-2 border-transparent focus:border-indigo-500"
                                value={advanceForm.employee_id}
                                onChange={e => setAdvanceForm({ ...advanceForm, employee_id: e.target.value })}
                            >
                                <option value="">Select Employee...</option>
                                {payouts.map(p => <option key={p.employee_id} value={p.employee_id}>{p.employee_name}</option>)}
                            </select>

                            <div className="relative">
                                <IndianRupee className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input
                                    required
                                    type="number"
                                    placeholder="Amount"
                                    className="w-full p-5 pl-12 bg-slate-50 rounded-3xl font-black text-xl outline-none"
                                    value={advanceForm.amount}
                                    onChange={e => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                                />
                            </div>

                            <input
                                type="date"
                                className="w-full p-5 bg-slate-50 rounded-3xl font-bold outline-none"
                                value={advanceForm.date}
                                onChange={e => setAdvanceForm({ ...advanceForm, date: e.target.value })}
                            />

                            <textarea
                                placeholder="Reason (e.g. For daughter's school fees)"
                                className="w-full p-5 bg-slate-50 rounded-3xl font-medium text-sm h-24 outline-none"
                                value={advanceForm.notes}
                                onChange={e => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                            />

                            <button className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-xl hover:bg-indigo-600 transition-all">
                                Confirm Cash Payment
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdvanceAndFinalize;